import { useTranslation } from 'react-i18next';
import { Grid, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import moment from 'moment';
import { useLanguage } from '../../common/GeneralUtils';
import { getDateFormat, getTimeFormat } from '../../common/getDateAndTimeFormat';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { OrderPageIntent } from '../OrderPageIntent';
import TimeUtils from '../../common/TimeUtils';
import { AccountData, OrderPageData } from '../../api';
import { MULTIPLE_TICKETS_DETAILS_FIELD_NAME } from '../OrderUtils';

type OrderFinalDetailsTableProps = {
  shouldShowDuration: boolean;
  orderPageIntent: OrderPageIntent;
  orderPageState: {
    dateAndTimeDetails: {
      date: string;
      time?: number;
    };
    details: {
      persons: number;
      duration: number;
      custom_details?: {
        workshopType?: string;
        [MULTIPLE_TICKETS_DETAILS_FIELD_NAME]?: Record<string, number>;
      };
    };
    orderPage: OrderPageData;
  };
  account: AccountData;
};

export const OrderFinalDetailsTable = ({
  orderPageIntent,
  shouldShowDuration,
  orderPageState,
  account,
}: OrderFinalDetailsTableProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const timeFormat = getTimeFormat(account);
  const objectWorkShopType = orderPageState.orderPage.objects.find((x: any) => x.name === 'workshopType');
  const selectedWorkshopType = orderPageState.details.custom_details?.workshopType;
  const objectMultipleTickets = orderPageState.orderPage.objects.find((x: any) => x.type === 'multiple_tickets_price');
  const multipleTicketsDetails = orderPageState.details.custom_details?.[MULTIPLE_TICKETS_DETAILS_FIELD_NAME];
  // @ts-ignore
  const multipleTickets = objectMultipleTickets?.tickets?.find((x: any) => x.id === selectedWorkshopType);
  const renderTableRow = (label: string, value: React.ReactNode) => (
    <TableRow
      sx={{
        '& td': {
          border: 'none',
          padding: '0px 16px',
        },
      }}>
      <TableCell width="80px">
        <OrderPageTypography variant={'h6'} bold>
          {label}
        </OrderPageTypography>
      </TableCell>
      <TableCell>
        <OrderPageTypography variant={'h6'}>{value}</OrderPageTypography>
      </TableCell>
    </TableRow>
  );

  return (
    <Grid container flexDirection="column" gap={0.5} mt={1}>
      <OrderPageTypography variant="h3" bold>
        {t('orderdetails')}
      </OrderPageTypography>
      <TableContainer>
        <Table size="small">
          <TableBody>
            {orderPageIntent === OrderPageIntent.BUY_NOW && (
              <>
                {renderTableRow(
                  t('order_page.date'),
                  moment(orderPageState.dateAndTimeDetails.date).format(getDateFormat(account).longDateFormat),
                )}
                {renderTableRow(
                  t('order_page.time', { activityLabelSingular: account?.activity_label_singular?.[language] || '' }),
                  moment
                    .unix(orderPageState.dateAndTimeDetails?.time || 0)
                    .tz('utc')
                    .format(timeFormat),
                )}
              </>
            )}
            {renderTableRow(t('order_page.persons'), orderPageState.details.persons)}
            {shouldShowDuration &&
              renderTableRow(
                t('order_page.duration', {
                  activityLabelSingular: account?.activity_label_singular?.[language] || '',
                }),
                TimeUtils.formatDuration(orderPageState.details.duration, t),
              )}
            {objectWorkShopType &&
              selectedWorkshopType &&
              renderTableRow(
                t('order_page.workshop'),
                // @ts-ignore
                objectWorkShopType.options?.find((x: any) => x.value === selectedWorkshopType)?.label[language],
              )}
            {objectMultipleTickets && multipleTicketsDetails && (
              <TableRow>
                <TableCell>
                  <OrderPageTypography bold>{t('order_page.workshopChoice')}</OrderPageTypography>
                </TableCell>
                <TableCell>
                  <Grid container flexDirection="column" gap={1}>
                    {Object.entries(multipleTicketsDetails).map(([key, value]) => (
                      <Grid container key={key} gap={1}>
                        <OrderPageTypography>{multipleTickets}:</OrderPageTypography>
                        <OrderPageTypography>{value}</OrderPageTypography>
                      </Grid>
                    ))}
                  </Grid>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};
